/**
 * Which build of the store app is running. A store app carries its web app
 * inside the package: an installed build keeps running until its user
 * updates it, so the backend may meet old builds for a long time.
 *
 * - Every API call says which build calls (`X-Chouette-App: android 0.1.0+3`).
 * - The backend gives the oldest build it still serves
 *   (GET /api/chouette/app-config, like Empilo's); below it, the app asks for
 *   an update (UpdateGate.tsx). Until the backend has the route, the app
 *   runs normally.
 *
 * The build number is the Android versionCode (the CI run number,
 * .github/workflows/android.yml) or the iOS CFBundleVersion. Nothing of this
 * runs in a browser: the site is always the latest.
 */
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { isNative } from "./platform";

export type Platform = "android" | "ios";
export type AppBuild = { platform: Platform; version: string; build: number };

let current: AppBuild | null = null;

/** Read once at start-up (main.tsx), before the first API call. */
export async function loadAppBuild(): Promise<AppBuild | null> {
  if (!isNative()) return null;
  try {
    const info = await App.getInfo();
    current = {
      platform: Capacitor.getPlatform() === "ios" ? "ios" : "android",
      version: info.version,
      build: Number.parseInt(info.build, 10) || 0,
    };
  } catch {
    current = null;
  }
  return current;
}

export function appBuild(): AppBuild | null {
  return current;
}

/** The header every API call carries in a store app; none in a browser. */
export function appHeaders(): Record<string, string> {
  return current ? { "X-Chouette-App": `${current.platform} ${current.version}+${current.build}` } : {};
}

/** Whether the backend no longer serves this build. */
export function isTooOld(app: AppBuild, minBuild: Partial<Record<Platform, number>>): boolean {
  return app.build < (minBuild[app.platform] ?? 0);
}

/**
 * Asks the backend whether this build is still served. Only a clear "too
 * old" answers true: no network, an error, or a backend without the route
 * lets the app run.
 */
export async function fetchTooOld(app: AppBuild, apiBase: string, fetcher: typeof fetch = fetch): Promise<boolean> {
  try {
    const res = await fetcher(`${apiBase}/chouette/app-config`, { headers: appHeaders() });
    if (!res.ok) return false;
    const config = (await res.json()) as { min_build?: Partial<Record<Platform, number>> };
    return !!config?.min_build && isTooOld(app, config.min_build);
  } catch {
    return false;
  }
}

/** Where to update. No App Store link until the app has its App Store id. */
export const STORE_URL: Record<Platform, string | null> = {
  android: "https://play.google.com/store/apps/details?id=app.memoneo.chouette",
  ios: null,
};

/** For the tests. */
export function setAppBuild(app: AppBuild | null): void {
  current = app;
}
